'use client'

import Link from 'next/link'
import { ListingCard } from '@/components/marketing/content/listing-card'
import { ListingGrid } from '@/components/marketing/content/listing-grid'
import { ListingPagination } from '@/components/marketing/content/listing-pagination'
import { ListingSearch } from '@/components/marketing/content/listing-search'
import { BeforeAfterCover } from '@/components/marketing/content/before-after-cover'
import { track } from '@/lib/analytics/track'
import { buildListingHref, LISTING_PAGE_SIZE } from '@/lib/marketing/pagination'

interface Tool {
  slug: string
  title: string
  description: string
  category: string
  coverImage?: string
}

const BASE_PATH = '/tools'

const beforeAfterSlots: Record<string, { before: string; after: string; alt: string }> = {
  'ai-background-remover': {
    before: 'https://cdn.soku.ai/marketing/images/tools/ai-background-remover/demo-before.jpg',
    after: 'https://cdn.soku.ai/marketing/images/tools/ai-background-remover/demo-after.png',
    alt: 'AI Background Remover',
  },
  'ai-face-swap': {
    before: 'https://cdn.soku.ai/marketing/images/tools/ai-face-swap/images/swap-before.png',
    after: 'https://cdn.soku.ai/marketing/images/tools/ai-face-swap/images/swap-after.png',
    alt: 'AI Face Swap',
  },
}

export function ToolsListing({
  tools,
  categories,
  activeCategory,
  query,
  total,
  page,
  totalPages,
}: {
  tools: Tool[]
  categories: string[]
  activeCategory: string
  /** Normalized `?q=`. Empty string when the visitor is not searching. */
  query: string
  /** Matches across every page, not just the cards rendered here. */
  total: number
  page: number
  totalPages: number
}) {
  const pills = [
    { id: 'all', label: 'All' },
    ...categories.map((c) => ({ id: c, label: c })),
  ]

  return (
    <>
      <ListingSearch
        basePath={BASE_PATH}
        category={activeCategory}
        defaultValue={query}
        label="Search tools"
        placeholder="Search tools — try “video”, “upscale”, “ROAS”"
        resultCount={total}
        onSearch={(q) => q && track('tools_search', { query: q })}
      />

      {/* Category pills — real ?category= links; selecting one resets to page 1 */}
      <div className="mb-8 flex flex-wrap gap-3">
        {pills.map((cat) => {
          const isActive = cat.id === activeCategory
          return (
            <Link
              key={cat.id}
              href={buildListingHref(BASE_PATH, {
                category: cat.id,
                q: query,
              })}
              scroll={false}
              onClick={() => track('tools_filter', { category: cat.id })}
              className={`rounded-xl border px-5 py-3 text-sm font-medium transition-all ${
                isActive
                  ? 'border-foreground bg-foreground text-background shadow-md'
                  : 'border-border bg-background text-muted-foreground hover:border-foreground/20 hover:bg-muted hover:text-foreground'
              }`}
            >
              {cat.label}
            </Link>
          )
        })}
      </div>

      {tools.length === 0 ? (
        <p className="py-16 text-center text-muted-foreground">
          No tools match{' '}
          <span className="font-medium text-foreground">
            “{query}”
          </span>
          {activeCategory !== 'all' && <> in {activeCategory}</>}.{' '}
          <Link
            href={buildListingHref(BASE_PATH, {})}
            className="text-foreground underline underline-offset-4"
          >
            Clear filters
          </Link>
        </p>
      ) : (
        <ListingGrid>
          {tools.map((tool, index) => {
          const ba = beforeAfterSlots[tool.slug]
          return (
            <ListingCard
              key={tool.slug}
              href={`/tools/${tool.slug}`}
              title={tool.title}
              description={tool.description}
              category={tool.category}
              image={tool.coverImage}
              imageSlot={
                ba ? (
                  <BeforeAfterCover
                    before={ba.before}
                    after={ba.after}
                    alt={ba.alt}
                  />
                ) : undefined
              }
              onClick={() =>
                track('tools_card_click', {
                  slug: tool.slug,
                  position: (page - 1) * LISTING_PAGE_SIZE + index + 1,
                })
              }
            />
          )
        })}
        </ListingGrid>
      )}

      <ListingPagination
        basePath={BASE_PATH}
        page={page}
        totalPages={totalPages}
        category={activeCategory}
        q={query}
      />
    </>
  )
}
